import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../stores/userStore";
import { isBetaCompActive } from "../lib/tier";

const DISMISS_KEY = "cosmic-finder:beta-comp-notice-dismissed";
const COMP_END = new Date("2026-07-08T07:00:00Z").getTime();
const WINDOW_MS = 14 * 86400000; // show within 2 weeks of comp end (campaign window)

/**
 * Dismissible heads-up for comped beta testers as the comp end approaches.
 * Self-gates: renders null unless beta + comp active + within 10 days + not
 * dismissed. Plan choice (monthly/yearly) lives on /upgrade, so "Continue as
 * Cosmic" routes there rather than starting checkout inline. Safe to mount
 * anywhere.
 */
export function BetaCompNotice() {
  const profile = useUserStore((s) => s.profile);
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (!profile || !isBetaCompActive(profile) || dismissed) return null;
  const within = COMP_END - Date.now() <= WINDOW_MS;
  if (!within) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      className="rounded-2xl border border-accent-border bg-linear-to-b from-bg-card to-bg-warm p-4"
      data-testid="beta-comp-notice"
    >
      <p className="text-sm text-text-primary leading-relaxed">
        Your founding access ends <span className="text-accent-fg">July 8</span>.
        Add a card now to keep your decks, journal, and ceremonies — you won't be
        charged until then, and you can cancel anytime. Your data is always saved.
      </p>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => navigate("/upgrade?from=founding")}
          className="px-4 py-2 rounded-2xl bg-accent-bg border border-accent-border text-accent-fg tracking-[0.18em] uppercase text-xs font-medium hover:bg-gold/20 transition-colors"
        >
          Continue as Cosmic
        </button>
        <button
          onClick={dismiss}
          className="px-3 py-2 text-xs text-text-secondary"
        >
          Later
        </button>
      </div>
    </div>
  );
}
